import {
  Button,
  Page,
  PageBody,
  PageHeader,
  PageHeaderTitle,
  PageHeaderDescription,
  PageHeaderActions,
  PageHeaderTabs,
} from '@customerio/pluma-components/react';
import { Helmet } from 'react-helmet-async';
import { Navigate } from 'react-router-dom';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useAuth } from '@/services/auth';
import { PlanFeature } from '@/services/auth/types';
import { hasUnlimitedPlanFeatureAccess } from '@/services/auth/plan-features';
import { FeedbackModal, useFeedbackModal } from '@/components/feedback-modal';
import { useAnalytics } from '@/services/analytics';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { AnalysisTabs } from '../components/analysis-tabs';
import { DeliverabilityTable } from './components/deliverability-table';
import { CustomSMTPEmptyState } from './components/custom-smtp-empty-state';
import { useDeliverabilityData, useHasMailServers } from './hooks';

export function DeliverabilityPage() {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const auth = useAuth();
  const feedbackModal = useFeedbackModal();
  const { track } = useAnalytics();

  const hasUnlimitedAccess = hasUnlimitedPlanFeatureAccess(auth.account, PlanFeature.DeliverabilityAnalytics);

  const {
    rows,
    dateRange,
    sorting,
    search,
    isLoading,
    isError,
    hasNoData,
    handleDateRangeChange,
    handleSortingChange,
    handleSearchChange,
  } = useDeliverabilityData();

  const { hasMailServers, isLoading: isLoadingMailServers } = useHasMailServers();

  // Track page view
  usePageViewTracking(() => {
    track('deliverability', 'viewed', {
      workspaceId,
      hasUnlimitedAccess,
    });
  }, true);

  // Check access
  if (!hasUnlimitedAccess) {
    // Redirect to main analysis page
    return <Navigate to={`/workspaces/${workspaceId}/journeys/analysis`} replace />;
  }

  const isLoadingPage = isLoadingMailServers || (isLoading && rows.length === 0);

  return (
    <>
      <Helmet>
        <title>Deliverability | Customer.io</title>
      </Helmet>
      <Page style={{ width: '100%' }}>
        <PageHeader>
          <PageHeaderTitle>Analysis</PageHeaderTitle>
          <PageHeaderDescription>
            View, compare, and analyze metrics across your entire workspace.
          </PageHeaderDescription>
          <PageHeaderActions>
            <Button variant="secondary" onClick={() => feedbackModal.show()}>
              Give Feedback
            </Button>
          </PageHeaderActions>
          <PageHeaderTabs>
            <AnalysisTabs />
          </PageHeaderTabs>
        </PageHeader>
        <PageBody isLoading={isLoadingPage}>
          {!isLoadingPage && hasMailServers ? (
            <CustomSMTPEmptyState />
          ) : !isLoadingPage ? (
            <DeliverabilityTable
              data={rows}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              isLoading={isLoading}
              isError={isError}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              search={search}
              onSearchChange={handleSearchChange}
              hasNoData={hasNoData}
            />
          ) : null}
        </PageBody>
      </Page>
      <FeedbackModal feature="Deliverability Analytics" eventCategory="deliverability" />
    </>
  );
}
